import { Fragment, useState } from "react";
import { Button } from "react-bootstrap";
import { Applicant } from "src/types/Applicant";
import {
  ApplicationHoldEventId,
  ApplicationStatusId,
  ApplicationCharge,
  ReduxApplication,
} from "src/types/Application";
import {
  capitalize,
  formatCurrency,
  formatDate,
  isLeaseDomainApplicationId,
} from "src/utils/Utils";
import { isApplicationHoldClosed } from "src/utils/HoldClock";
import HoldClock from "../Shared/HoldClock";
import CancelApplicationModal from "./CancelApplicationModal";
import ResetHoldClockModal from "./ResetHoldClockModal";
import { hasUnresolvedHighRisk } from "src/utils/Application";
import { Configuration } from "src/types/Redux";
import { useAppSelector } from "src/hooks/redux";
import TriangleWarningIcon from "src/components/Shared/icons/TriangleWarningIcon";
import { ApplicantType } from "src/types/Applicant";
import { getIsLeaseDomainEnabledForPhase } from "src/services/AdminDashService";

// Lease Domain gates hold-clock extension on the primary applicant's real controls.canExtendHold
// (false for a fully locked-down applicant, e.g. cancelled due to Customer Risk exclusion) -
// legacy has no equivalent concept, so this only applies when the phase is Lease Domain-enabled.
const getPrimaryApplicantCanExtendHold = (application: ReduxApplication) =>
  application.applicants?.find(
    (a) => a.applicantTypeId === ApplicantType.Applicant && !a.isSoftDelete
  )?.controls?.canExtendHold ?? false;

interface LeftPanelProps {
  application: ReduxApplication;
  shouldRenderSecurityDepositSection: boolean;
  refreshApplicationData: any;
  secDepositPaymentStatus: string | undefined | null;
}

const LeftPanel = ({
  application,
  refreshApplicationData,
  shouldRenderSecurityDepositSection,
}: LeftPanelProps) => {
  const {
    communityName,
    completeUnitId,
    primaryApplicantFirstName,
    primaryApplicantLastName,
    moveInDate,
    leaseTerm,
    applicants,
    monthlyCharges,
    parking,
    applicationHold,
    isSfQuickMoveIn,
    applicationId,
    applicationStatusId,
    canAssociateCancelApplication,
    hasHousingVoucher,
    leaseSecurityDeposit,
    appliedConcessions,
    areAppliedConcessionsInMri,
  } = application;
  const [selectedApplicant, setSelectedApplicant] = useState<Applicant>();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showResetHoldClockModal, setShowResetHoldClockModal] = useState(false);
  const openCancelApplication = () => {
    setShowCancelModal(true);
    const applicant = applicants.filter(
      (a) =>
        a.firstName === primaryApplicantFirstName &&
        a.lastName === primaryApplicantLastName
    );

    if (applicant && applicant.length > 0) {
      setSelectedApplicant(applicant[0]);
    }
  };

  const resetHoldClock = () => {
    setShowResetHoldClockModal(true);
  };

  const showHoldClock = // isClockRunning equivalent
    applicationHold && !isApplicationHoldClosed(applicationHold);

  const configuration: Configuration =
    useAppSelector((state) => state.applications.configuration) ??
    ({} as Configuration);

  const isPhaseLeaseDomainEnabled = getIsLeaseDomainEnabledForPhase(
    configuration,
    application.phaseId
  );

  const canExtendHoldClock = // isExtendable equivalent
    applicationHold &&
    showHoldClock &&
    !applicationHold.isExpired &&
    applicationHold.updateEventId !== ApplicationHoldEventId.ADMIN_ADJUSTED &&
    (!isPhaseLeaseDomainEnabled ||
      getPrimaryApplicantCanExtendHold(application));

  const isHighRiskApplication = hasUnresolvedHighRisk(
    application,
    configuration
  );

  const renderHoldClock = (application) => {
    if (!showHoldClock) {
      return <></>;
    }

    return (
      <div className="left-panel-hold-clock-container">
        <HoldClock application={application} type={"detail"} />
        <Button
          className="reset-button"
          variant="outline-light"
          disabled={!canExtendHoldClock}
          onClick={resetHoldClock}
        >
          Reset Clock
        </Button>
      </div>
    );
  };

  const nonCancellableStatuses = [
    ApplicationStatusId.Completed,
    ApplicationStatusId.Cancelled,
    ApplicationStatusId.LeaseReady,
    ApplicationStatusId.LeasePending,
    ApplicationStatusId.LeaseSigned,
    ApplicationStatusId.LeaseDeclined,
    ApplicationStatusId.LeaseExpired,
  ];
  // Lease Domain's flag already says whether generation is in flight, so only terminal statuses block it.
  const leaseDomainTerminalStatuses = [
    ApplicationStatusId.Completed,
    ApplicationStatusId.Cancelled,
    ApplicationStatusId.LeaseSigned,
    ApplicationStatusId.LeaseDeclined,
    ApplicationStatusId.LeaseExpired,
    ApplicationStatusId.Removed,
    ApplicationStatusId.ApplicationComplete,
  ];
  // Keyed on the application id, not the phase: an LD-enabled phase can still hold legacy applications.
  const isLeaseDomainApplication = isLeaseDomainApplicationId(
    String(applicationId ?? "")
  );
  const isCancellable = isLeaseDomainApplication
    ? !leaseDomainTerminalStatuses.includes(applicationStatusId) &&
      canAssociateCancelApplication === true
    : !!!nonCancellableStatuses.find(
        (value) => value === applicationStatusId
      ) && canAssociateCancelApplication !== false;

  // include `status` field as override to ApplicationCharge `amount` and include parking `status` as needed
  interface RenderedMonthlyCharge extends ApplicationCharge {
    status?: string;
  }
  let renderedMonthlyCharges: RenderedMonthlyCharge[] = [
    ...(monthlyCharges || []),
  ];
  if (parking && !parking.optInApplicantId) {
    // Display parking status field since monthly charges won't contain an amount
    renderedMonthlyCharges = [
      ...monthlyCharges,
      {
        label: "Parking charge/rent",
        chgCode: "PKD",
        amount: 0,
        header: "",
        status: (parking.optOutApplicantId && "Opted-Out") || "Not Selected",
      },
    ];
  }

  const getRoundedAmount = (val: number | undefined): number | null => {
    return typeof val === "number" && !isNaN(val)
      ? Math.round(val * 100) / 100
      : null;
  };

  // AP2-6877: each Security Deposit line (unit, and the pet/addon deposit increase when one
  // exists) gates its own Paid/Not Paid pill independently - a combined payment accepted for both
  // still resolves each line's own paidAmount share to Paid, since the formatter already split it.
  const buildDepositLine = (
    deposit:
      | { requiredAmount: number | undefined; paidAmount: number | undefined }
      | null
      | undefined
  ) => {
    const requiredAmount = getRoundedAmount(deposit?.requiredAmount);
    const paidAmount = getRoundedAmount(deposit?.paidAmount);
    const isPaid =
      !!requiredAmount && !!paidAmount && paidAmount >= requiredAmount;
    return {
      requiredAmount,
      pillClass: isPaid
        ? "pill-security-deposit"
        : "pill-security-deposit-unpaid",
      pillLabel: isPaid ? "Paid" : "Not Paid",
    };
  };

  // Legacy applications return the flat {requiredAmount, paidAmount} shape, so use it as the unit line.
  const unitDepositLine = buildDepositLine(
    leaseSecurityDeposit?.unitDeposit ??
      (leaseSecurityDeposit?.requiredAmount !== undefined ||
      leaseSecurityDeposit?.paidAmount !== undefined
        ? {
            requiredAmount: leaseSecurityDeposit.requiredAmount,
            paidAmount: leaseSecurityDeposit.paidAmount,
          }
        : undefined)
  );
  const petDepositLine = leaseSecurityDeposit?.petDeposit
    ? buildDepositLine(leaseSecurityDeposit.petDeposit)
    : null;

  const renderSpecialsSection = () => {
    if (appliedConcessions == null || appliedConcessions.length === 0) {
      return null;
    }
    return (
      <div className="specials-container">
        {areAppliedConcessionsInMri === false &&
          appliedConcessions.length > 0 && (
            <div className="specials-alert">
              <div className="icon">
                <TriangleWarningIcon aria-hidden="true" />
              </div>
              <div>
                <div className="title">Action Required:</div>
                <div className="subtitle">Enter specials into MRI</div>
              </div>
            </div>
          )}

        {appliedConcessions?.map((concession, index) => (
          <div className="special" key={concession.id}>
            <div className="label">Special {index + 1}:</div>
            <div className="text">
              <strong>{concession.title}</strong>
              <div>{concession.description}</div>
            </div>
          </div>
        ))}
        <div className="divider" />
      </div>
    );
  };

  return (
    <div className="col-sm-12 col-md-3 left-panel">
      <h2 className="row heading text-uppercase my-3">
        Unit APPLICATION Information
      </h2>
      {renderHoldClock(application)}
      <div className="label">Community</div>
      <div className="text">{capitalize(communityName)}</div>
      <div className="label">Unit</div>
      <div className="text">
        {completeUnitId}

        {hasHousingVoucher ? (
          <span
            className="badge rounded-pill pill-quick-move-in"
            title="Housing Voucher"
          >
            HOU
          </span>
        ) : (
          <></>
        )}
      </div>
      <div className="label">Primary Applicant</div>
      <div className="text">
        {primaryApplicantFirstName || primaryApplicantLastName
          ? `${primaryApplicantFirstName || ""} ${
              primaryApplicantLastName || ""
            }`.trim()
          : "N/A"}
      </div>
      <div className="label">Move-In Date</div>
      <div className="text">
        {formatDate(moveInDate)}{" "}
        {isSfQuickMoveIn ? (
          <span className="badge rounded-pill pill-quick-move-in">
            Quick Move-in!
          </span>
        ) : (
          <></>
        )}
      </div>
      <div className="label">Lease Term</div>
      <div className="text">{leaseTerm}</div>
      {shouldRenderSecurityDepositSection &&
        unitDepositLine.requiredAmount !== null &&
        unitDepositLine.requiredAmount > 0 && (
          <>
            <div className="label">Security Deposit</div>
            <div className="text">
              {formatCurrency(unitDepositLine.requiredAmount)}
              <span
                className={`badge rounded-pill ms-2 ${unitDepositLine.pillClass}`}
              >
                {unitDepositLine.pillLabel}
              </span>
            </div>
          </>
        )}
      {shouldRenderSecurityDepositSection &&
        petDepositLine &&
        petDepositLine.requiredAmount !== null &&
        petDepositLine.requiredAmount > 0 && (
          <>
            <div className="label">Pet Deposit Increase</div>
            <div className="text">
              {formatCurrency(petDepositLine.requiredAmount)}
              <span
                className={`badge rounded-pill ms-2 ${petDepositLine.pillClass}`}
              >
                {petDepositLine.pillLabel}
              </span>
            </div>
          </>
        )}
      {renderedMonthlyCharges
        ? renderedMonthlyCharges
            .sort((a, b) =>
              a.label === "Total Monthly Charges" ? -1 : b.amount - a.amount
            )
            .map((charge, index) => {
              const { label, chgCode, header, amount, status } = charge;
              // render `status` if present instead of `amount`
              let feeFieldContent: JSX.Element | string | null =
                status ||
                `${formatCurrency(amount)}${
                  header?.toLowerCase() === "monthly" ? "/monthly" : ""
                }`;
              if (
                parking &&
                parking.optInApplicantId &&
                parking.reservationUrl &&
                chgCode.toLowerCase().includes("pkd")
              ) {
                feeFieldContent = (
                  <a
                    id="parking-reservation"
                    style={{ color: "#00a3e0" }}
                    href={`${parking.reservationUrl}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {feeFieldContent}
                  </a>
                );
              }

              return (
                <Fragment key={`charge-${index}`}>
                  <div className="label">{label}</div>
                  <div className="text">{feeFieldContent}</div>
                </Fragment>
              );
            })
        : null}
      <>
        <div className="divider" />
        {renderSpecialsSection()}
      </>
      {isCancellable && (
        <button className="cancel" onClick={openCancelApplication}>
          Cancel this Application
        </button>
      )}
      <CancelApplicationModal
        selectedApplicant={selectedApplicant}
        selectedApplication={application}
        isHighRiskApplication={false} // Normal associates don't need the standard or high risk distinction, so this value is not used in the CancelApplicationModal for associates. The modal will just display the regular cancellation modal
        configuration={configuration}
        onCancel={() => setShowCancelModal(false)}
        show={showCancelModal}
      />
      <ResetHoldClockModal
        selectedApplication={application}
        configuration={configuration}
        onCancel={() => setShowResetHoldClockModal(false)}
        show={showResetHoldClockModal}
        refreshApplicationData={refreshApplicationData}
      />
    </div>
  );
};

export default LeftPanel;
